import { type MantineColor } from '@mantine/core';
import {
    Status,
    Priority,
    TraderOrderStatus,
    PayoutEventType,
    PublicStatus,
    SplitPart,
    CascadeEventType,
    CascadeGroupSkipReason,
} from './model';

export const orderPriorityColors = {
    [Priority.PRIORITY_UNSPECIFIED]: 'gray.5',
    [Priority.PRIORITY_HIGH]: 'red.6',
    [Priority.PRIORITY_MEDIUM]: 'orange.5',
    [Priority.PRIORITY_LOW]: 'green.5',
} as const;

export const orderPriorities = [
    {
        value: Priority.PRIORITY_UNSPECIFIED,
        label: 'Не определен',
    },
    {
        value: Priority.PRIORITY_LOW,
        label: 'Низкий',
    },
    {
        value: Priority.PRIORITY_MEDIUM,
        label: 'Средний',
    },
    {
        value: Priority.PRIORITY_HIGH,
        label: 'Высокий',
    },
];

export const traderOrderStatusColors: Record<TraderOrderStatus, MantineColor> = {
    [TraderOrderStatus.STATUS_UNSPECIFIED]: 'gray.6',
    [TraderOrderStatus.STATUS_WAITING_REACTION]: 'orange.5',
    [TraderOrderStatus.STATUS_IN_WORK]: 'blue.5',
    [TraderOrderStatus.STATUS_DECLINED]: 'red.6',
    [TraderOrderStatus.STATUS_EXPIRED]: 'gray.5',
    [TraderOrderStatus.STATUS_COMPLETE]: 'green.6',
    [TraderOrderStatus.STATUS_ALREADY_TAKEN]: 'violet.5',
    [TraderOrderStatus.STATUS_INVESTIGATION]: 'yellow.5',
    [TraderOrderStatus.STATUS_APPROVED]: 'blue.5',
    [TraderOrderStatus.STATUS_INVESTIGATION_APPROVED]: 'green.6',
    [TraderOrderStatus.STATUS_INVESTIGATION_DECLINED]: 'red.6',
    [TraderOrderStatus.STATUS_SPLIT]: 'violet.6',
    [TraderOrderStatus.STATUS_CANCELLED]: 'red.5',
} as const;

export const traderOrderStatuses = [
    {
        value: TraderOrderStatus.STATUS_WAITING_REACTION,
        label: 'В ожидании',
    },
    {
        value: TraderOrderStatus.STATUS_IN_WORK,
        label: 'В работе',
    },
    {
        value: TraderOrderStatus.STATUS_EXPIRED,
        label: 'Просрочен',
    },
    {
        value: TraderOrderStatus.STATUS_ALREADY_TAKEN,
        label: 'Упущен',
    },
    {
        value: TraderOrderStatus.STATUS_APPROVED,
        label: 'Одобрен',
    },
    {
        value: TraderOrderStatus.STATUS_COMPLETE,
        label: 'Финализирован',
    },
    {
        value: TraderOrderStatus.STATUS_DECLINED,
        label: 'Отклонен',
    },
    {
        value: TraderOrderStatus.STATUS_INVESTIGATION,
        label: 'На рассмотрении AML',
    },
    {
        value: TraderOrderStatus.STATUS_INVESTIGATION_APPROVED,
        label: 'Одобрен AML',
    },
    {
        value: TraderOrderStatus.STATUS_INVESTIGATION_DECLINED,
        label: 'Отклонен AML',
    },
    {
        value: TraderOrderStatus.STATUS_SPLIT,
        label: 'Разделен',
    },
    {
        value: TraderOrderStatus.STATUS_CANCELLED,
        label: 'Отменен',
    },
    {
        value: TraderOrderStatus.STATUS_UNSPECIFIED,
        label: 'Не указан',
    },
];

export const orderStatusColors: Record<Status, MantineColor> = {
    [Status.STATUS_UNSPECIFIED]: 'gray.6',
    [Status.STATUS_CHOOSING_TRADER]: 'indigo.5',
    [Status.STATUS_WAITING_TRADER_RESPONSE]: 'orange.5',
    [Status.STATUS_EXPIRED]: 'gray.5',
    [Status.STATUS_IN_WORK]: 'blue.5',
    [Status.STATUS_APPROVED]: 'blue.5',
    [Status.STATUS_COMPLETE]: 'green.6',
    [Status.STATUS_INVESTIGATION]: 'yellow.5',
    [Status.STATUS_INVESTIGATION_DECLINED]: 'red.6',
    [Status.STATUS_INVESTIGATION_APPROVED]: 'green.6',
    [Status.STATUS_SPLIT]: 'violet.6',
    [Status.STATUS_CANCELLED]: 'red.5',
} as const;

export const orderStatuses = [
    {
        value: Status.STATUS_CHOOSING_TRADER,
        label: 'Поиск трейдера',
    },
    {
        value: Status.STATUS_WAITING_TRADER_RESPONSE,
        label: 'Ожидание ответа',
    },
    {
        value: Status.STATUS_IN_WORK,
        label: 'В работе',
    },
    {
        value: Status.STATUS_EXPIRED,
        label: 'Просрочен',
    },
    {
        value: Status.STATUS_APPROVED,
        label: 'Одобрен',
    },
    {
        value: Status.STATUS_COMPLETE,
        label: 'Финализирован',
    },
    {
        value: Status.STATUS_INVESTIGATION,
        label: 'На рассмотрении AML',
    },
    {
        value: Status.STATUS_INVESTIGATION_APPROVED,
        label: 'Одобрен AML',
    },
    {
        value: Status.STATUS_INVESTIGATION_DECLINED,
        label: 'Отклонен AML',
    },
    {
        value: Status.STATUS_SPLIT,
        label: 'Разделен',
    },
    {
        value: Status.STATUS_CANCELLED,
        label: 'Отменен',
    },
    {
        value: Status.STATUS_UNSPECIFIED,
        label: 'Не указан',
    },
];

export const orderEventTypes: Record<PayoutEventType, string> = {
    [PayoutEventType.TYPE_UNSPECIFIED]: 'Неизвестное событие',
    [PayoutEventType.TYPE_ORDER_CREATED]: 'Ордер создан',
    [PayoutEventType.TYPE_TRADER_CHOSEN]: 'Трейдер выбран',
    [PayoutEventType.TYPE_TRADER_DECLINED]: 'Трейдер отклонил ордер',
    [PayoutEventType.TYPE_TRADER_ACCEPTED]: 'Трейдер принял ордер',
    [PayoutEventType.TYPE_TRADER_ORDER_EXPIRED]: 'Время ордера трейдера истекло',
    [PayoutEventType.TYPE_ORDER_EXPIRED]: 'Срок ордера истёк',
    [PayoutEventType.TYPE_ORDER_UPDATED]: 'Ордер обновлён',
    [PayoutEventType.TYPE_ORDER_FAILED]: 'Ошибка выполнения ордера',
    [PayoutEventType.TYPE_ORDER_COMPLETED]: 'Ордер завершён',
    [PayoutEventType.TYPE_START_INVESTIGATION]: 'Начато расследование',
    [PayoutEventType.TYPE_INVESTIGATION_APPROVED]: 'Расследование одобрено',
    [PayoutEventType.TYPE_INVESTIGATION_DECLINED]: 'Расследование отклонено',
    [PayoutEventType.TYPE_APPROVED]: 'Ордер подтверждён',
    [PayoutEventType.TYPE_ORDER_SPLIT]: 'Ордер разделен',
};

export const orderCascadeEventTypes: Record<CascadeEventType, string> = {
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_UNSPECIFIED]: 'Неизвестное событие',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_CASCADE_MATCHED]: 'Каскад найден',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_NO_CASCADE_MATCHED]: 'Каскад не найден',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_GROUP_REQUESTED]: 'Группа запрошена',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_GROUP_SKIPPED]: 'Группа пропущена',
};

export const orderCascadeEventTypeColors: Record<CascadeEventType, MantineColor> = {
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_UNSPECIFIED]: 'gray.6',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_CASCADE_MATCHED]: 'green.6',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_NO_CASCADE_MATCHED]: 'red.6',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_GROUP_REQUESTED]: 'blue.5',
    [CascadeEventType.ORDER_CASCADE_EVENT_TYPE_GROUP_SKIPPED]: 'orange.5',
};

export const orderCascadeGroupSkipReasons: Record<CascadeGroupSkipReason, string> = {
    [CascadeGroupSkipReason.ORDER_CASCADE_GROUP_SKIP_REASON_UNSPECIFIED]: 'Неизвестная причина',
    [CascadeGroupSkipReason.ORDER_CASCADE_GROUP_SKIP_REASON_QUEUE_FULL]: 'Очередь группы заполнена',
    [CascadeGroupSkipReason.ORDER_CASCADE_GROUP_SKIP_REASON_NO_ACTIVE_TRADERS]: 'Нет активных трейдеров',
    [CascadeGroupSkipReason.ORDER_CASCADE_GROUP_SKIP_REASON_NO_TRADER_CONFIG_MATCHED]:
        'Нет подходящих конфигов трейдеров',
    [CascadeGroupSkipReason.ORDER_CASCADE_GROUP_SKIP_REASON_ALL_TRADERS_DECLINED]:
        'Все подходящие трейдеры уже отклоняли этот ордер',
    [CascadeGroupSkipReason.ORDER_CASCADE_GROUP_SKIP_REASON_ALL_TRADERS_HAVE_DUPLICATE_ORDER_MASK]:
        'У всех подходящих трейдеров уже есть ожидающий ордер с такой же маской',
};

export const publicStatusColors: Record<PublicStatus, MantineColor> = {
    [PublicStatus.PUBLIC_STATUS_UNSPECIFIED]: 'gray.6',
    [PublicStatus.PUBLIC_STATUS_CREATED]: 'blue.5',
    [PublicStatus.PUBLIC_STATUS_PENDING]: 'orange.5',
    [PublicStatus.PUBLIC_STATUS_COMPLETED]: 'green.6',
    [PublicStatus.PUBLIC_STATUS_FAILED]: 'red.7',
} as const;

export const publicStatuses = [
    {
        value: PublicStatus.PUBLIC_STATUS_CREATED,
        label: 'Создан',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_PENDING,
        label: 'В ожидании',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_COMPLETED,
        label: 'Завершен',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_FAILED,
        label: 'Ошибка',
    },
    {
        value: PublicStatus.PUBLIC_STATUS_UNSPECIFIED,
        label: 'Не указан',
    },
];

export const splitParts = [
    {
        value: SplitPart.SPLIT_PART_UNSPECIFIED,
        label: 'Не указана',
    },
    {
        value: SplitPart.SPLIT_PART_BODY,
        label: 'Хвост',
    },
    {
        value: SplitPart.SPLIT_PART_TAIL,
        label: 'Тело',
    },
];
